import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Toggle — migrated from Radix to Base UI
 *
 * Base UI Toggle is a flat component (not namespaced with .Root).
 *
 * Data attribute changes:
 *   data-state="on"  → data-pressed
 *   data-state="off" → (no attribute)
 *
 * onPressedChange:
 *   Radix:   (pressed: boolean) => void
 *   Base UI: (pressed: boolean, eventDetails) => void
 *   We wrap to preserve the Radix-compatible single-arg signature.
 *
 * DS alignment (Phase 3A):
 *   - Icon default: size-5 (20px = DS icon-md)
 *   - Pressed state: data-[pressed]:bg-accent (fill-action-lightest = purple-050)
 *     with data-[pressed]:text-accent-foreground
 *   - Hover: bg-muted (fill-neutral-lightest = neutral-050) for unpressed
 *   - Focus ring: ring-ring/50 resolves to border-focus (purple-300)
 *   - Disabled: opacity-50
 *
 * Size mapping:
 *   default — h-9 min-w-9 (36px) = DS Medium
 *   sm      — h-8 min-w-8 (32px) = DS Small
 *   lg      — h-10 min-w-10 (40px) = DS Large
 */

const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[pressed]:bg-accent data-[pressed]:text-accent-foreground dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: 'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-9 min-w-9 px-2',
        sm: 'h-8 min-w-8 px-1.5',
        lg: 'h-10 min-w-10 px-2.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export namespace Toggle {
  export interface Props
    extends
      Omit<React.ComponentProps<typeof TogglePrimitive>, 'onPressedChange' | 'render'>,
      VariantProps<typeof toggleVariants> {
    /** Callback fired when the pressed state changes. */
    onPressedChange?: (pressed: boolean) => void
  }
}

function Toggle({ className, variant, size, onPressedChange, ...props }: Toggle.Props) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      onPressedChange={onPressedChange ? (pressed) => onPressedChange(pressed) : undefined}
      {...props}
    />
  )
}

export { Toggle, toggleVariants }
